/**
 * 공유 풀 **종료 정리**(close cleanup) 계약.
 *
 * 공유 풀에 미래 일정이 남아 있으면 종료가 막혀 있었다. 이제 서버가 "무엇이 취소되는가"를
 * 먼저 보여주고(close-preview), 관리자가 **그 목록 그대로** 확인하면 취소하고 종료한다.
 *
 * 되돌릴 수 없는 동작이다. 취소된 수업은 복구되지 않고 회원에게 알림이 나간다.
 * 그래서 이 파일의 규칙은 전부 "애매하면 멈춘다" 쪽으로 되어 있다.
 *
 * 근거: 백엔드 `membershipShare.service.ts` / `membershipShareView.service.ts`
 * (`origin/develop` `e3871c33`).
 */

export type SharePoolCloseItem = {
    /** 'SCHEDULE'(수업) | 'REQUEST'(대기 신청). 값이 늘어도 화면이 깨지지 않게 열어 둔다. */
    kind: 'SCHEDULE' | 'REQUEST' | (string & {});
    id: string;
    startTime: string;
};

export type SharePoolCloseMemberImpact = {
    memberId: string;
    role: 'OWNER' | 'PARTICIPANT' | (string & {});
    /** 센터 별칭 우선. 서버가 못 찾으면 빈 문자열로 온다. */
    name: string;
    scheduleCount: number;
    requestCount: number;
    totalCount: number;
    items: SharePoolCloseItem[];
};

export type SharePoolCloseImpact = {
    sharePoolId: string;
    /** 'MEMBERSHIP'(PT) | 'MEMBER_TICKET'(그룹). 그룹은 정리를 지원하지 않는다. */
    sourceType: string;
    asOf: string;
    /**
     * 자동 정리를 지원하는가. **그룹(MEMBER_TICKET) 풀은 false 다.**
     * 그룹 예약 취소는 대기자 자동 승격 때문에 되돌릴 수 없고, 예약 writer 와
     * 직렬화되지도 않는다. 백엔드가 조회조차 하지 않는다.
     */
    supportsCleanup: boolean;
    cancelableCount: number;
    owner: SharePoolCloseMemberImpact | null;
    participants: SharePoolCloseMemberImpact[];
};

/** 서버 Joi 상한(`expectedItemIds` max 1000). 넘으면 400 이라 화면이 먼저 막아야 한다. */
export const SHARE_CLOSE_EXPECTED_ITEM_ID_LIMIT = 1000;

/**
 * 회원 삭제는 id 를 **쿼리스트링**으로 보낸다(DELETE 에 본문을 쓰지 않는다).
 *
 * id 하나가 24자 + 구분자라 1000개면 URL 이 25KB 가 된다. 프록시·서버의 흔한 URL
 * 한계(8KB)를 넘어 요청이 통째로 거절되고, 그 실패는 서버 로직에 닿기도 전이라
 * 원인이 화면에 드러나지 않는다. 그래서 종료(PATCH 본문)보다 낮게 잡는다.
 */
export const SHARE_DELETE_EXPECTED_ITEM_ID_QUERY_LIMIT = 200;

const readCount = (value: unknown): number | undefined =>
    typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : undefined;

const readText = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

/**
 * 항목 하나. **id 가 없으면 통째로 실패시킨다.**
 *
 * 여기서 조용히 걸러내면 "화면에 보여준 목록"과 "보내는 expectedItemIds" 가 갈린다.
 * 그러면 서버가 확인 안 된 항목으로 보고 409 를 내거나, 더 나쁘게는 관리자가 못 본
 * 수업이 취소된다. 하나라도 이상하면 프리뷰 전체를 버리고 종료 버튼을 열지 않는다.
 */
const readItem = (value: unknown): SharePoolCloseItem | null => {
    if (!value || typeof value !== 'object') return null;
    const raw = value as Record<string, unknown>;
    const id = readText(raw.id);
    const kind = readText(raw.kind);
    if (!id || !kind) return null;
    return { kind, id, startTime: readText(raw.startTime) };
};

const readMemberImpact = (
    value: unknown,
    fallbackRole: 'OWNER' | 'PARTICIPANT',
): SharePoolCloseMemberImpact | null => {
    if (!value || typeof value !== 'object') return null;
    const raw = value as Record<string, unknown>;
    const scheduleCount = readCount(raw.scheduleCount);
    const requestCount = readCount(raw.requestCount);
    const totalCount = readCount(raw.totalCount);
    if (scheduleCount === undefined || requestCount === undefined || totalCount === undefined) {
        return null;
    }
    // items 가 배열이 아니면 계약 위반이다. 빈 배열로 흘리면 취소 대상이 조용히 사라진다.
    if (raw.items !== undefined && !Array.isArray(raw.items)) return null;
    const rawItems = Array.isArray(raw.items) ? raw.items : [];
    const items: SharePoolCloseItem[] = [];
    for (const rawItem of rawItems) {
        const item = readItem(rawItem);
        if (!item) return null;
        items.push(item);
    }
    /**
     * 같은 id 가 두 번 오면 **통째로 버린다.**
     *
     * 화면 건수는 items 길이로 세고 전송은 id 집합(중복 제거)이라, 중복이 있으면
     * "2건 취소" 라고 해놓고 1건만 보낸다. 표시와 전송이 갈리는 유일하게 남은 원인이라
     * 여기서 끊는다.
     */
    if (new Set(items.map((item) => item.id)).size !== items.length) return null;
    /**
     * 서버가 "취소할 게 있다"고 세어 놓고 목록을 안 줬으면 **계약 위반이다.**
     *
     * 그대로 읽으면 화면이 "취소 대상 없음"으로 보이고 빈 `expectedItemIds` 를 보내,
     * 서버가 실제 대상을 확인 안 된 것으로 보고 거절한다. 관리자는 왜 안 되는지
     * 모른 채 반복하게 된다. 부분 배포된 서버에서 실제로 날 수 있는 조합이다.
     */
    if (totalCount > 0 && items.length === 0) return null;
    return {
        memberId: readText(raw.memberId),
        role: readText(raw.role) || fallbackRole,
        name: readText(raw.name),
        scheduleCount,
        requestCount,
        totalCount,
        items,
    };
};

/**
 * `GET /v1/membership-share-pools/:sharePoolId/close-preview` 응답을 읽는다.
 *
 * 형태가 어긋나면 `null`. 호출부는 그때 **종료 버튼을 열지 않는다** — 구버전 서버
 * (이 엔드포인트가 없어 404)와 같은 취급이라, 오늘처럼 막힌 상태로 남는다.
 */
export const readSharePoolCloseImpact = (body: unknown): SharePoolCloseImpact | null => {
    if (!body || typeof body !== 'object') return null;
    const raw = (body as { closeImpact?: unknown }).closeImpact;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;

    const impact = raw as Record<string, unknown>;
    const cancelableCount = readCount(impact.cancelableCount);
    const supportsCleanup = impact.supportsCleanup;
    // 판정에 쓰는 값이라 추측하지 않는다. 없으면 통째로 버린다.
    if (typeof supportsCleanup !== 'boolean' || cancelableCount === undefined) return null;

    /**
     * 소유자도 참여자와 같은 규칙이다 — **읽지 못하면 통째로 버린다.**
     *
     * 예전에는 `owner: null` 로 두고 나머지를 돌려줬는데, 그러면 소유자의 취소 대상이
     * `expectedItemIds` 에서 조용히 빠진다. 서버가 그 항목을 확인 안 된 것으로 보고
     * 거절하면 그나마 낫고, 더 나쁘게는 관리자가 소유자 수업이 취소되는 줄 모른 채
     * 승인하게 된다. 서버는 항상 owner 를 만들어 보내므로, 없으면 계약 위반이다.
     */
    const owner = readMemberImpact(impact.owner, 'OWNER');
    if (!owner) return null;
    /**
     * `participants` 가 배열이 아니면 **통째로 버린다.**
     *
     * 빈 배열로 흘리면 참여자가 조용히 사라진다 — 화면에는 소유자만 나오는데 서버는
     * 참여자 항목까지 취소 대상으로 들고 있다. owner 와 같은 규칙이어야 한다.
     */
    if (impact.participants !== undefined && !Array.isArray(impact.participants)) return null;
    const rawParticipants = Array.isArray(impact.participants) ? impact.participants : [];
    const participants: SharePoolCloseMemberImpact[] = [];
    for (const rawParticipant of rawParticipants) {
        const participant = readMemberImpact(rawParticipant, 'PARTICIPANT');
        if (!participant) return null;
        participants.push(participant);
    }

    return {
        sharePoolId: readText(impact.sharePoolId),
        sourceType: readText(impact.sourceType),
        asOf: readText(impact.asOf),
        supportsCleanup,
        cancelableCount,
        owner,
        participants,
    };
};

/** 소유자 먼저, 그다음 참여자. 화면 표시 순서와 전송 순서를 같게 쓴다. */
export const listSharePoolCloseMemberImpacts = (
    impact: SharePoolCloseImpact | null | undefined,
): SharePoolCloseMemberImpact[] => {
    if (!impact) return [];
    return [impact.owner, ...impact.participants].filter(
        (usage): usage is SharePoolCloseMemberImpact => !!usage,
    );
};

/**
 * `expectedItemIds` — 소유자와 참여자 **전원**의 `items[].id` 합집합.
 *
 * **화면에서 접거나 생략한 항목도 반드시 들어간다.** 하나라도 빠지면 서버가
 * `SHARE_CLOSE_PLAN_CHANGED` 로 거절한다. 그래서 화면 표시용 목록을 따로 만들지 않고
 * 같은 `items` 를 쓴다 — 두 벌이 되면 보여준 것과 보내는 것이 갈린다.
 */
export const collectSharePoolCloseExpectedItemIds = (
    impact: SharePoolCloseImpact | null | undefined,
): string[] => {
    const seen = new Set<string>();
    const ids: string[] = [];
    listSharePoolCloseMemberImpacts(impact).forEach((usage) => {
        usage.items.forEach((item) => {
            if (seen.has(item.id)) return;
            seen.add(item.id);
            ids.push(item.id);
        });
    });
    return ids;
};

/**
 * 회원 한 명의 취소 건수를 **`items` 에서 직접 센다.**
 *
 * 서버가 `scheduleCount`/`requestCount`/`totalCount` 를 함께 주지만 그 숫자로 화면을
 * 그리면 안 된다. 전송되는 것은 `items` 이고, 두 축이 어긋나면 **화면에 없는 항목이
 * 취소되거나 "2건"이라 해놓고 3건이 취소된다.** 되돌릴 수 없는 동작에서 표시와 전송이
 * 갈리는 자리는 만들지 않는다. 서버 카운트는 참고용으로만 둔다.
 */
export const countSharePoolCloseItems = (
    items: readonly SharePoolCloseItem[],
): { scheduleCount: number; requestCount: number; totalCount: number } => {
    let scheduleCount = 0;
    let requestCount = 0;
    items.forEach((item) => {
        if (item.kind === 'SCHEDULE') scheduleCount += 1;
        else if (item.kind === 'REQUEST') requestCount += 1;
    });
    return { scheduleCount, requestCount, totalCount: items.length };
};

/** 정리 대상이 서버 상한을 넘는가. 넘으면 400 이라 저장을 시도조차 하지 않는다. */
export const isSharePoolCloseTooLarge = (
    impact: SharePoolCloseImpact | null | undefined,
): boolean =>
    collectSharePoolCloseExpectedItemIds(impact).length > SHARE_CLOSE_EXPECTED_ITEM_ID_LIMIT;

/* ------------------------------------------------------------------ *
 * 종료 실패 안내
 * ------------------------------------------------------------------ */

export type SharePoolCloseFailure = {
    title: string;
    message: string;
    /**
     * **서버가 선언한 `retryable` 을 그대로 옮긴다.** 다른 필드에서 파생하지 않는다.
     * "같은 요청으로 다시 시도해도 되나"는 상태값으로 추론할 수 없다 — 예전에
     * 공유 1:2 에서 그렇게 파생했다가 무한 루프를 만들었다.
     */
    retryable: boolean;
    /** 프리뷰를 다시 받아야 하는가. 확인 목록이 낡았을 때만 true. */
    needsRefreshedPreview: boolean;
};

const readErrorCode = (data: unknown): string => {
    if (!data || typeof data !== 'object') return '';
    const errorCode = (data as { errorCode?: unknown }).errorCode;
    return typeof errorCode === 'string' ? errorCode.trim() : '';
};

const readErrorDetails = (data: unknown): Record<string, unknown> => {
    if (!data || typeof data !== 'object') return {};
    const details = (data as { details?: unknown }).details;
    return details && typeof details === 'object' ? (details as Record<string, unknown>) : {};
};

/**
 * **이미 취소된 건이 있으면 반드시 말한다.**
 *
 * `..._CLEANUP_INCOMPLETE` 와 `..._BLOCKED_BY_PENDING_APPROVAL` 은 `canceledCount` 가
 * 0 이 아닐 수 있다 — 일부는 이미 취소된 뒤에 막힌 것이다. 그 취소는 되돌아오지 않고
 * 회원에게 알림도 이미 나갔다. "실패했다"로만 말하면 관리자는 아무 일도 없었다고
 * 오해한다. (`SHARE_CLOSE_PLAN_CHANGED` 만 항상 0 이다.)
 */
const describeCanceledSoFar = (details: Record<string, unknown>): string => {
    /**
     * `canceledCount` 는 **실패한 그 풀에서만** 센 수다.
     *
     * 회원 삭제는 소유 풀을 순서대로 닫는다. 앞선 풀들이 이미 취소·종료된 뒤 다음
     * 풀에서 실패하면 서버가 그 합계를 `alreadyCanceledCount` 로 따로 실어 준다.
     * 그걸 안 읽으면 "취소된 수업은 없어요"라고 **거짓으로 단정**하게 된다.
     */
    const total =
        (readCount(details.canceledCount) ?? 0) + (readCount(details.alreadyCanceledCount) ?? 0);
    if (total === 0) return '';
    return `\n이미 ${total}건이 취소되었고 되돌릴 수 없어요. 해당 회원에게는 알림이 나갔어요.`;
};

/**
 * **알 수 없는 실패에도 이미 취소된 것이 있을 수 있다.**
 *
 * 서버 `closeOwnedSharePoolsForMemberRemoval` 의 catch 는 errorCode 와 무관하게
 * **모든 오류**에 `alreadyCanceledCount` 를 붙인다(DB 오류·일반 500 포함). 아는 코드
 * 셋만 보면 그 경로에서 "삭제 실패"로만 안내하게 되고, 관리자는 아무 일도 없었다고
 * 믿는다 — 그 사이 회원은 취소 알림을 받는다.
 *
 * 값이 없거나 0이면 빈 문자열이라 기존 안내가 그대로 나간다.
 */
export const describeAlreadyCanceledOnUnknownFailure = (data: unknown): string => {
    const details = readErrorDetails(data);
    const alreadyCanceledCount = readCount(details.alreadyCanceledCount) ?? 0;
    if (alreadyCanceledCount === 0) return '';
    return `\n\n이미 ${alreadyCanceledCount}건의 수업이 취소되었고 되돌릴 수 없어요. 해당 회원에게는 알림이 나갔어요.`;
};

/** 이번 실패 전에 이미 취소된 것이 있는가. "아무것도 안 됐다"고 쓸 수 있는지 가른다. */
const hasAnyCanceled = (details: Record<string, unknown>): boolean =>
    (readCount(details.canceledCount) ?? 0) + (readCount(details.alreadyCanceledCount) ?? 0) > 0;

/**
 * 서버가 선언한 `retryable` 을 그대로 읽는다. **다른 필드에서 파생하지 않는다.**
 * 값이 없으면 `false` — "다시 눌러도 된다"는 서버가 말해줄 때만 참이다.
 * 되돌릴 수 없는 취소 앞에서 모르면 안 된다는 쪽이 안전하다.
 */
const readRetryable = (details: Record<string, unknown>): boolean =>
    details.retryable === true;

const describeRemaining = (details: Record<string, unknown>): string => {
    const remainingCount = readCount(details.remainingCount);
    if (remainingCount === undefined || remainingCount === 0) return '';
    return ` 남은 ${remainingCount}건은 아직 정리되지 않았어요.`;
};

/**
 * 공유 풀 종료 실패를 사람이 읽을 안내로 바꾼다. 아는 코드가 아니면 `null` —
 * 호출부의 기존 처리가 그대로 이어진다.
 */
export const resolveSharePoolCloseFailure = (
    data: unknown,
): SharePoolCloseFailure | null => {
    const details = readErrorDetails(data);
    switch (readErrorCode(data)) {
        /**
         * 확인한 뒤에 새 예약이 들어와 중단됐다. **아무것도 취소되지 않았다**
         * (`canceledCount: 0` 이 그 뜻이다). 데이터가 망가진 것이 아니므로
         * "실패"로 끝내지 않고 다시 확인받으면 된다고 말한다.
         */
        case 'SHARE_CLOSE_PLAN_CHANGED': {
            const unconfirmedCount = readCount(details.unconfirmedCount);
            return {
                title: '확인한 뒤에 예약이 바뀌었어요',
                message: `확인하신 목록에 없던 예약이 ${
                    unconfirmedCount !== undefined ? `${unconfirmedCount}건 ` : ''
                }새로 잡혔어요.${
                    hasAnyCanceled(details)
                        ? `${describeCanceledSoFar(details)}\n이번 요청에서 추가로 취소된 수업은 없어요.`
                        : '\n취소된 수업은 없어요.'
                }\n무엇이 취소되는지 다시 확인한 뒤 종료해주세요.`,
                retryable: readRetryable(details),
                needsRefreshedPreview: true,
            };
        }

        /**
         * 승인 처리가 중단된 신청이 막고 있다. **같은 요청을 다시 보내도 안 풀린다.**
         * 서버가 `retryable: false` 와 `recoverBy` 로 명시한다. 재시도 버튼을 주면
         * 관리자가 계속 누르게 되고 그때마다 되돌릴 수 없는 취소가 더 일어난다.
         */
        case 'SHARE_POOL_CLOSE_BLOCKED_BY_PENDING_APPROVAL': {
            const blockedRequestIds = Array.isArray(details.blockedRequestIds)
                ? details.blockedRequestIds.filter((id) => typeof id === 'string')
                : [];
            return {
                title: '처리가 멈춘 신청이 있어 종료할 수 없어요',
                message: `승인 도중 멈춘 수업 신청 ${
                    blockedRequestIds.length > 0 ? `${blockedRequestIds.length}건이 ` : ''
                }남아 있어요.${describeRemaining(details)}${describeCanceledSoFar(
                    details,
                )}\n다시 시도해도 같은 결과예요. 신청 목록에서 그 신청을 먼저 처리하거나 운영 담당자에게 확인해주세요.`,
                retryable: readRetryable(details),
                needsRefreshedPreview: false,
            };
        }

        /** 일부만 정리됐다. 서버가 `retryable: true` — 같은 요청을 다시 보내면 이어서 정리한다. */
        case 'SHARE_POOL_CLOSE_CLEANUP_INCOMPLETE':
            return {
                title: '일부만 정리되어 종료하지 못했어요',
                message: `일부 수업을 정리하지 못했어요.${describeRemaining(
                    details,
                )}${describeCanceledSoFar(details)}\n${
                    readRetryable(details)
                        ? '잠시 후 같은 내용으로 다시 시도해주세요.'
                        : '화면을 새로고침해 남은 예약을 확인한 뒤 다시 진행해주세요.'
                }`,
                retryable: readRetryable(details),
                needsRefreshedPreview: false,
            };

        default:
            return null;
    }
};

/* ------------------------------------------------------------------ *
 * 회원 삭제 — 소유 풀 정리
 *
 * ⚠️ **이 절의 함수들은 지금 화면에서 쓰이지 않는다. 지우지 마라.**
 *
 * 회원 삭제 자동 정리는 **별건으로 분리**됐다(2026-09-01). 교차검증 3회차까지 그 경로에서
 * CRITICAL 이 반복해서 나왔고 — 늦게 도착한 응답이 다른 회원의 취소 목록을 화면에 되살려
 * **엉뚱한 회원이 삭제될 수 있었다** — 되돌릴 수 없는 동작이라 이번 배포에서 뺐다.
 *
 * 뺄 수 있었던 이유: 서버의 삭제 차단은 `status: 'ACTIVE'` 풀만 조회한다
 * (`membershipShareIdentity.service.ts`). **공유를 종료하면 차단이 자동으로 풀린다.**
 * 관리자는 종료 → 삭제 두 단계로 목적을 달성하므로 막히지 않는다.
 *
 * 여기 있는 것들은 그 별건 작업이 **그대로 다시 쓴다.** 테스트도 함께 남겨 뒀다.
 * 배경과 반복된 결함 유형은 `TICKET_BULK_MEMBER_DELETE_SWALLOWS_409.md` 참고.
 * ------------------------------------------------------------------ */

/**
 * `SHARE_OWNER_DISCONNECT_BLOCKED` 의 `details.sharePoolIds`.
 *
 * 삭제하려는 회원이 소유한 **활성 공유 풀**이다. 이 풀들을 정리해 종료해야 삭제가 된다.
 * 아는 코드가 아니거나 id 가 없으면 `null` — 호출부는 정리 흐름을 열지 않는다.
 */
export const readShareOwnerDisconnectPoolIds = (data: unknown): string[] | null => {
    if (readErrorCode(data) !== 'SHARE_OWNER_DISCONNECT_BLOCKED') return null;
    const raw = readErrorDetails(data).sharePoolIds;
    if (!Array.isArray(raw)) return null;
    const ids = raw
        .map((id) => (typeof id === 'string' ? id.trim() : ''))
        .filter((id) => id.length > 0);
    return ids.length > 0 ? ids : null;
};

/**
 * 여러 풀의 프리뷰를 하나의 확인 계획으로 합친다.
 *
 * 회원 삭제는 그 회원이 소유한 풀을 **전부** 닫는다. 서버가 받는 `expectedItemIds` 도
 * **풀 구분 없는 합집합**이다. 그래서 화면도 합쳐서 보여준다 — 풀별로 나눠 보여주면
 * 관리자가 "몇 명의 수업이 취소되는가"를 세지 못한다.
 */
export type ShareOwnerCleanupPlan = {
    /** 정리 대상 풀. 프리뷰를 못 받은 풀이 하나라도 있으면 계획을 만들지 않는다. */
    impacts: SharePoolCloseImpact[];
    /** 회원별로 합친 취소 대상. 소유자 먼저, 그다음 참여자. */
    memberRows: Array<{
        memberId: string;
        role: string;
        name: string;
        scheduleCount: number;
        requestCount: number;
        totalCount: number;
    }>;
    expectedItemIds: string[];
    totalCount: number;
    /** 자동 정리를 지원하지 않는 풀이 섞여 있는가(그룹 풀). 있으면 진행할 수 없다. */
    hasUnsupportedPool: boolean;
    /** 서버 상한을 넘는가. */
    isTooLarge: boolean;
    /**
     * 풀·회원을 가로질러 같은 항목 id 가 두 번 나왔는가. **계약 위반이다.**
     *
     * 파서는 회원 한 행 안의 중복만 거부한다. 소유자·참여자 사이나 여러 풀 사이의
     * 중복은 여기서만 보인다. 조용히 버리면 화면 인원·건수와 실제 취소 문서 수가
     * 갈리므로, 진행을 막고 사람이 보게 한다(정상 응답에는 없다).
     */
    hasDuplicateItemIds: boolean;
};

export const buildShareOwnerCleanupPlan = (
    impacts: SharePoolCloseImpact[],
): ShareOwnerCleanupPlan => {
    const byMember = new Map<string, ShareOwnerCleanupPlan['memberRows'][number]>();
    const seenIds = new Set<string>();
    const expectedItemIds: string[] = [];
    let hasDuplicateItemIds = false;

    impacts.forEach((impact) => {
        listSharePoolCloseMemberImpacts(impact).forEach((usage) => {
            // 같은 회원이 두 풀에 걸쳐 있을 수 있다. 사람 기준으로 합쳐야 "누구의 수업이
            // 몇 건 취소되는가"가 맞는다.
            const key = usage.memberId || `${usage.role}:${usage.name}`;
            /**
             * 건수는 **전송 목록에 실제로 들어간 항목만** 센다.
             *
             * 파서가 회원 안의 중복은 막지만 **풀 간 중복**은 남는다. 그때 items 길이로
             * 세면 화면은 2건인데 전송(id 집합)은 1건이 된다. 표시와 전송은 한 축이어야
             * 하므로, 중복 제거를 통과한 항목만 카운트에 반영한다.
             */
            const acceptedItems = usage.items.filter((item) => {
                if (seenIds.has(item.id)) {
                    // 조용히 버리지 않는다. 표시와 전송이 갈리는 계약 위반 신호다.
                    hasDuplicateItemIds = true;
                    return false;
                }
                seenIds.add(item.id);
                expectedItemIds.push(item.id);
                return true;
            });
            const counts = countSharePoolCloseItems(acceptedItems);
            const existing = byMember.get(key);
            if (existing) {
                existing.scheduleCount += counts.scheduleCount;
                existing.requestCount += counts.requestCount;
                existing.totalCount += counts.totalCount;
                // 한 풀에서 소유자면 소유자로 표시한다.
                if (usage.role === 'OWNER') existing.role = 'OWNER';
            } else {
                byMember.set(key, {
                    memberId: usage.memberId,
                    role: usage.role,
                    name: usage.name,
                    ...counts,
                });
            }
        });
    });

    const memberRows = Array.from(byMember.values())
        .filter((row) => row.totalCount > 0)
        .sort((a, b) => (a.role === 'OWNER' ? -1 : b.role === 'OWNER' ? 1 : 0));

    return {
        impacts,
        memberRows,
        expectedItemIds,
        totalCount: expectedItemIds.length,
        hasUnsupportedPool: impacts.some((impact) => !impact.supportsCleanup),
        // 삭제 경로는 쿼리스트링 상한을 쓴다 — 종료(본문)보다 훨씬 낮다.
        isTooLarge: expectedItemIds.length > SHARE_DELETE_EXPECTED_ITEM_ID_QUERY_LIMIT,
        hasDuplicateItemIds,
    };
};
